import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  const lastUpdate = "14 de marzo de 2026";

  return (
    <div className="min-h-screen bg-audioyou-bg">
      {/* Header */}
      <header className="neumorphic-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-audioyou-accent flex items-center justify-center">
                <span className="text-white text-xl">🎙️</span>
              </div>
              <span className="text-xl font-bold text-gray-800">AudioToo</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="neumorphic-button-small inline-flex items-center gap-2 px-3 py-2 mb-6">
          <ArrowLeft size={16} />
          <span>Volver</span>
        </Link>

        <div className="neumorphic-card p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Términos de Uso</h1>
          <p className="text-sm text-gray-500 mb-8">Última actualización: {lastUpdate}</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Aceptación de los Términos</h2>
              <p className="text-gray-600 leading-relaxed">
                Al acceder y utilizar AudioToo ("la Plataforma"), aceptas estos Términos de Uso. 
                Si no estás de acuerdo con alguna parte de estos términos, no debes usar la Plataforma.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Descripción del Servicio</h2>
              <p className="text-gray-600 leading-relaxed">
                AudioToo es una plataforma de micro-audios que permite a los usuarios grabar, compartir 
                y descubrir contenido de audio de corta duración (5-59 segundos). Los audios se clasifican 
                mediante un sistema visual único de formas, colores, emojis e idiomas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. Registro y Cuenta</h2>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Debes tener al menos 13 años para usar AudioToo.</li>
                <li>Eres responsable de mantener la seguridad de tu cuenta.</li>
                <li>Una persona solo puede tener una cuenta.</li>
                <li>Debes proporcionar información veraz al registrarte.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Contenido del Usuario</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Al publicar contenido en AudioToo, declaras que:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Eres el propietario o tienes los derechos necesarios sobre el contenido.</li>
                <li>El contenido no infringe derechos de terceros.</li>
                <li>El contenido no es ilegal, difamatorio, obsceno o dañino.</li>
                <li>Otorgas a AudioToo una licencia no exclusiva para mostrar y distribuir tu contenido.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Contenido Prohibido</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Está prohibido publicar contenido que:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Promueva violencia, odio o discriminación.</li>
                <li>Contenga material sexual explícito.</li>
                <li>Infrinja derechos de propiedad intelectual.</li>
                <li>Sea spam, publicidad no autorizada o phishing.</li>
                <li>Contenga información falsa o engañosa.</li>
                <li>Viole la privacidad de terceros.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Propiedad Intelectual</h2>
              <p className="text-gray-600 leading-relaxed">
                AudioToo y su contenido original, características y funcionalidad son propiedad de 
                AudioToo y están protegidos por leyes de propiedad intelectual. Conservas los derechos 
                sobre el contenido que creas y publicas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Terminación</h2>
              <p className="text-gray-600 leading-relaxed">
                Podemos suspender o terminar tu acceso a AudioToo inmediatamente, sin previo aviso, 
                por cualquier motivo, incluyendo el incumplimiento de estos Términos. Puedes eliminar 
                tu cuenta en cualquier momento desde la configuración de tu perfil.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">8. Limitación de Responsabilidad</h2>
              <p className="text-gray-600 leading-relaxed">
                AudioToo se proporciona "tal cual" sin garantías de ningún tipo. No seremos responsables 
                de daños indirectos, incidentales o consecuentes derivados del uso de la Plataforma.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">9. Cambios en los Términos</h2>
              <p className="text-gray-600 leading-relaxed">
                Nos reservamos el derecho de modificar estos términos en cualquier momento. 
                Te notificaremos sobre cambios significativos. El uso continuado de la Plataforma 
                después de los cambios constituye tu aceptación de los nuevos términos.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">10. Contacto</h2>
              <p className="text-gray-600 leading-relaxed">
                Para preguntas sobre estos Términos de Uso, contáctanos en:{' '}
                <a href="mailto:legal@audioyou.app" className="text-audioyou-accent hover:underline">
                  legal@audioyou.app
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
